<template>
  <div class="roles-main">
    <el-card shadow="never" class="filter-card">
      <div class="search">
        <el-date-picker
          v-model="startDate"
          type="date"
          placeholder="Start Date"
          format="YYYY/MM/DD"
          value-format="YYYY-MM-DD"
          size="small"
        />
        <el-date-picker
          v-model="endDate"
          type="date"
          placeholder="End Date"
          format="YYYY/MM/DD"
          value-format="YYYY-MM-DD"
          size="small"
        />
        <el-button
          icon="el-icon-search"
          size="small"
          type="success"
          @click="getRoomStatistics()"
        >
          Search
        </el-button>
        <el-select
          clearable
          v-model="statisticsType"
          size="small"
          placeholder="user state"
          class="filter-item"
          style="width: 120px"
          @change="getRoomStatistics()"
        >
          <el-option
            v-for="item in uiControl.statisticsType"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </div>
    </el-card>
    <el-card shadow="never" class="section-card">
      <div class="card-head">
        <span class="card-title">Rooms Over Time</span>
        <span class="card-badge">Total Rooms: {{ totalCount }}</span>
      </div>
      <div class="chart-time" v-if="list.records.length > 0">
        <Chart :options="options" />
      </div>
    </el-card>
    <el-card shadow="never" class="section-card">
      <el-table
        v-loading="loading"
        :data="list.records"
        ref="table"
        row-key="id"
        size="small"
        border
        class="beauty-table"
        highlight-current-row
        empty-text="No data"
        :cell-class-name="cellGrayWhite"
      >
        <el-table-column
          label="Time"
          align="left"
          min-width="180"
          show-overflow-tooltip
        >
          <template #default="scope">
            <span v-if="statisticsType === statsType.DAY">
              {{ convertDate(scope.row.recordTime) }}
            </span>
            <span
              v-else
              v-formatter="{
                data: scope.row.recordTime,
                formatter: 'YYYY/MM/DD HH:mm:ss',
                type: 'date',
              }"
            />
          </template>
        </el-table-column>
        <el-table-column
          prop="totalCount"
          label="Rooms"
          header-align="right"
          align="right"
        >
          <template #default="scope">
            <span class="num-cell">{{ scope.row.totalCount }}</span>
          </template>
        </el-table-column>
        <el-table-column
          prop="missCount"
          label="Miss Room"
          header-align="right"
          align="right"
        >
          <template #default="scope">
            <span
              class="num-cell"
              :class="{'cell-red': scope.row.missCount > 0}"
            >
              {{ scope.row.missCount }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="Avg. Wait Time" header-align="right" align="right">
          <template #default="scope">
            <span class="num-cell">{{
              formatSeconds(scope.row.avgWaitMatchTime)
            }}</span>
          </template>
        </el-table-column>
        <el-table-column label="Avg. Chat Time" header-align="right" align="right">
          <template #default="scope">
            <span class="num-cell">{{
              formatSeconds(scope.row.avgFinishTime)
            }}</span>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref } from '@vue/runtime-core'
import { getRoomsStatisticsByTime, statsType } from '../../../../api/report'
import { showDateTime, convertDate, convertDateTime } from '@/utils/util'
import Chart from '@/components/charts/Charts'
import 'echarts/lib/component/tooltip'
import { cellGrayWhite } from '@/utils/style'
import { ElMessage } from 'element-plus'

const loading = ref(false)

function formatSeconds(v) {
  if (v === null || v === undefined || v === '') {
    return '—'
  }
  const n = Number(v)
  if (Number.isNaN(n)) {
    return '—'
  }
  if (n === 0) {
    return '0s'
  }
  if (n < 60) {
    return (Number.isInteger(n) ? n : n.toFixed(1)) + 's'
  }
  const total = Math.round(n)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  let str = ''
  if (h > 0) {
    str += h + 'h'
  }
  if (m > 0) {
    str += m + 'm'
  }
  if (s > 0) {
    str += s + 's'
  }
  return str || '0s'
}

const list = reactive({
  records: [],
})

const startDate = ref(convertDate(new Date()))
const endDate = ref(convertDate(new Date()))
const statisticsType = ref('DAY')

const totalCount = ref(0)
const uiControl = reactive({
  statisticsType: [
    {
      value: statsType.DAY,
      label: 'Day',
    },
    {
      value: statsType.HOUR,
      label: 'Hour',
    },
  ],
})

const options = reactive({
  series: [],
  tooltip: {
    trigger: 'axis',
  },
  legend: {
    data: [
      'Total Room',
      // 'Miss Room',
      'Avg.Wait Match Time',
      'Avg.Finish Match Time',
    ],
  },
  grid: {
    left: '3%',
    right: '4%',
    bottom: '3%',
    containLabel: true,
  },
  xAxis: {
    type: 'category',
    axisPointer: {
      type: 'shadow',
    },
    data: [],
  },
  yAxis: [
    {
      type: 'value',
      name: 'Count',
      alignTicks: true,
    },
    {
      type: 'value',
      name: 'Time (hrs)',
    },
  ],
})

async function getRoomStatistics() {
  loading.value = true
  try {
    const { data: res } = await getRoomsStatisticsByTime(
      startDate.value,
      endDate.value,
      statisticsType.value
    )
    list.records = res
    if (res.length > 0) {
      totalCount.value = res
        .map(stats => stats.totalCount)
        .reduce((acc, count) => acc + count)
      const categoryData = []
      const totalCountData = []
      const avgWaitMatchTimeData = []
      const avgFinishTimeData = []
      res.forEach((item, index) => {
        if (statisticsType.value === statsType.DAY) {
          categoryData.push(convertDate(item.recordTime))
        } else {
          categoryData.push(convertDateTime(item.recordTime))
        }
        totalCountData.push(item.totalCount)
        avgWaitMatchTimeData.push(item.avgWaitMatchTime)
        avgFinishTimeData.push(item.avgFinishTime)
      })
      options.xAxis.data = categoryData
      options.series = [
        {
          name: 'Total Room',
          type: 'bar',
          // stack: 'count',
          data: totalCountData,
        },
        {
          name: 'Avg.Wait Match Time',
          type: 'line',
          yAxisIndex: 1,
          data: avgWaitMatchTimeData,
          tooltip: {
            valueFormatter: function (value) {
              return showDateTime(value);
            }
          },
        },
        {
          name: 'Avg.Finish Match Time',
          type: 'line',
          yAxisIndex: 1,
          data: avgFinishTimeData,
          tooltip: {
            valueFormatter: function (value) {
              return showDateTime(value);
            }
          },
        },
      ]
    }
  } catch (e) {
    ElMessage.error('Failed to load data')
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  await getRoomStatistics()
})
</script>

<style scoped>
.filter-card {
  margin-bottom: 16px;
  border-radius: 8px;
}
.filter-card :deep(.el-card__body) {
  padding: 16px 18px;
}
.search {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  row-gap: 10px;
}
.search > *:not(h6) + * {
  margin-left: 20px;
}
.section-card {
  border-radius: 10px;
  margin-bottom: 16px;
}
.section-card :deep(.el-card__body) {
  padding: 16px 18px;
}
.card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}
.card-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--el-text-color-primary, #303133);
}
.card-badge {
  font-size: 12px;
  color: var(--el-text-color-secondary, #909399);
  font-variant-numeric: tabular-nums;
}
.beauty-table :deep(thead th) {
  background: #f5f7fa;
  font-weight: 600;
  color: var(--el-text-color-primary, #303133);
}
.num-cell {
  font-variant-numeric: tabular-nums;
}
.chart-time {
  /* width: 700px; */
  height: 400px;
  margin: auto;
}
</style>
